import React, { useRef, useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom'; // Import useLocation
import { walldata } from '../Config/wallcoveringkidsdata.js';
import Navbar from '../Components/Navbar';
import Footer from '../Components/Footer';
import Scrollfunctions from '../Components/Scrollfunctions';
import Shareooptions from '../Components/Shareooptions.js';
import TopSuggestions from '../Components/TopSuggestions.js';
import OurInformation from '../Components/OurInformation.js';
import { Undo2 } from 'lucide-react';
// import CollapsibleInfo from '../Components/collapsehandpaintings.js';
import useMetaTags from '../Components/useMetaTags.js';
import watermark from '../Images/watermark/WATERMARK-2.png';
import AddToWishlistButton from '../Components/AddToWishlistButton.js';

const Kidsdesc = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation(); // Initialize useLocation
  const imgRef = useRef(null);
  const selectedImage = walldata[id - 1];

  // Track the image actually being displayed (handles jpeg→jpg fallback)
  const [currentImageSrc, setCurrentImageSrc] = useState(selectedImage?.Img);
  const [imageError, setImageError] = useState(false);

  // Set dynamic meta tags for social media sharing
  useMetaTags({
    title: selectedImage ? `${selectedImage.title} - Kids Wallcovering | Vizaura` : 'Kids Wallcovering | Vizaura',
    description: selectedImage ? `${selectedImage.title} - ${selectedImage.desc}. ${selectedImage.context}` : 'Discover playful kids wallcoverings from Vizaura. Premium wallpapers, art prints, hand paintings, and custom interior graphics tailored for your space.',
    image: currentImageSrc || 'https://artfluence2.vercel.app/Images/vizaura_logo.png',
    url: window.location.href,
    type: 'website'
  });

  const isMobile = window.innerWidth <= 768; // Define isMobile here for direct use

  const pricingRules = {
    Normal: 120,
    Textured: 150,
    'Canvas texture': 200,
    'Rodies glitter': 200,
    Strokes: 200,
  };

  // Read material from URL on initial load
  const initialMaterial = new URLSearchParams(location.search).get('material') || 'Normal';
  const [selectedMaterial, setSelectedMaterial] = useState(initialMaterial);

  const [selectedPrice, setSelectedPrice] = useState(() => {
    // Set initial price based on initialMaterial
    return pricingRules[initialMaterial] || '120';
  });

  const [height, setHeight] = useState('');
  const [width, setWidth] = useState('');
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  const [showCustomOptions, setShowCustomOptions] = useState(false);
  const [isWide, setIsWide] = useState(window.innerWidth >= 1077);
  const [showMaterialDropdown, setShowMaterialDropdown] = useState(false); // New state for dropdown visibility

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Handle browser back button and mobile back button navigation
  useEffect(() => {
    console.log('[kidsdesc] Component mounted/updated with id:', id);
    
    // Store the current image ID when component mounts or id changes
    console.log('[kidsdesc] Storing id in sessionStorage:', id);
    sessionStorage.setItem('lastViewedImageIdWallcoveringkids', id);
    console.log('[kidsdesc] sessionStorage now contains:', sessionStorage.getItem('lastViewedImageIdWallcoveringkids'));

    const handleBeforeUnload = () => {
      // Store the current image ID in sessionStorage for back navigation
      console.log('[kidsdesc] Before unload - storing id:', id);
      sessionStorage.setItem('lastViewedImageIdWallcoveringkids', id);
    };

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup: Store ID when component unmounts (e.g., when navigating away)
    return () => {
      console.log('[kidsdesc] Component unmounting - storing id:', id);
      sessionStorage.setItem('lastViewedImageIdWallcoveringkids', id);
      console.log('[kidsdesc] After unmount, sessionStorage contains:', sessionStorage.getItem('lastViewedImageIdWallcoveringkids'));
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [id]);

  useEffect(() => {
    const handleResize = () => {
      setIsWide(window.innerWidth >= 1077);
    };

    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle image load error and fallback to jpg
  const handleImageError = () => {
    if (!imageError && selectedImage?.ImgJpg) {
      setCurrentImageSrc(selectedImage.ImgJpg);
      setImageError(true);
    }
  };

  const handleMaterialChange = (e) => {
    const newMaterial = e.target.value;
    setSelectedMaterial(newMaterial);
    updatePrice(newMaterial);

    // Update URL with the new material
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('material', newMaterial);
    navigate(`?${searchParams.toString()}`, { replace: true }); // Use replace to avoid extra history entries
    if (isMobile) {
      setShowMaterialDropdown(false); // Close dropdown after selection on mobile
    }
  };

  const updatePrice = (material) => {
    const newPrice = pricingRules[material] || 'N/A';
    setSelectedPrice(newPrice);
  };

  const handleHeightChange = (e) => {
    setHeight(e.target.value);
  };

  const handleWidthChange = (e) => {
    setWidth(e.target.value);
  };

  const calculatePrice = () => {
    if (!selectedMaterial || !height || !width) {
      alert('Please select a material and enter both height and width.');
      return;
    }
    const pricePerSqFt = pricingRules[selectedMaterial];
    const totalArea = (parseFloat(height) / 12) * (parseFloat(width) / 12); // Convert inches to feet for area calculation
    const totalPrice = totalArea * pricePerSqFt;
    setCalculatedPrice(Math.floor(totalPrice));
  };

  const handleMouseMove = (e) => {
    const img = imgRef.current;
    const rect = img.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const xPercent = (x / rect.width) * 100;
    const yPercent = (y / rect.height) * 100;
    img.style.transformOrigin = `${xPercent}% ${yPercent}%`;
    img.style.transform = 'scale(1.5)';
  };

  const handleMouseLeave = () => {
    const img = imgRef.current;
    img.style.transformOrigin = 'center';
    img.style.transform = 'scale(1)';
  };

  function useWindowWidth() {
    const [widths, setWidths] = useState(window.innerWidth);

    useEffect(() => {
      const handleResize = () => setWidths(window.innerWidth);
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);

    return widths;
  }

  const widths = useWindowWidth();
  const isMobileOrTablet = widths <= 1023;

  if (!selectedImage) {
    return navigate('/404');
  }

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <Navbar />
      <div className="container mx-auto pt-20 px-4 sm:px-6 lg:px-8">
        {/* Back button */}
        <div className="mb-2 ml-2 sm:mb-6 sm:ml-5">
          <button
            onClick={() => {
              console.log('[kidsdesc] Back button clicked - current id:', id);
              // Store the current ID before navigating (in case of refresh scenario)
              sessionStorage.setItem('lastViewedImageIdWallcoveringkids', id);
              console.log('[kidsdesc] Stored id in sessionStorage before navigation:', id);
              // Navigate with scrollTo parameter in URL
              const targetUrl = `/wallcoverings/kids?scrollTo=${id}`;
              console.log('[kidsdesc] Navigating to:', targetUrl);
              navigate(targetUrl);
            }}
            className="w-10 h-10 sm:w-14 sm:h-14 rounded-full bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700
        group hover:ring-2 hover:ring-gray-700 hover:ring-opacity-50 shadow-md hover:shadow-xl
        flex items-center justify-center transition-all duration-300"
            aria-label="Go Back"
          >
            <Undo2 className="w-4 h-4 sm:w-6 sm:h-6 text-white group-hover:scale-110 transition-transform duration-300" />
          </button>
        </div>


        {/* Main content grid */}
        <div className={`grid grid-cols-1 lg:grid-cols-2 ${isWide ? 'gap-12' : 'gap-6'} mb-16 items-start`}>
          {/* Left side - Image */}
          <div className="relative overflow-hidden rounded-xl shadow-xl shadow-gray-400">
            {/* Watermark overlay: © + image */}
            <div className="absolute bottom-4 right-4 flex items-center space-x-2 z-10 pointer-events-none">
              <span className="text-lg lg:text-2xl text-gray-200" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.4)' }}>©</span>
              <img
                src={watermark}
                alt="Watermark"
                className="w-24 lg:w-48 select-none"
              />
            </div>

            {/* Main image */}
            <img
              ref={imgRef}
              src={currentImageSrc}
              alt={selectedImage.title}
              className={
                isMobileOrTablet
                  ? "w-full h-auto max-h-screen object-cover transition-transform duration-300"
                  : "w-full h-auto max-h-screen object-contain transition-transform duration-300"
              }
              {...(!isMobileOrTablet && {
                onMouseMove: handleMouseMove,
                onMouseLeave: handleMouseLeave,
              })}
              onError={handleImageError}
              style={{
                transition: 'transform 0.4s ease-out, transform-origin 0.4s ease-out',
              }}
            />
          </div>

          {/* Right side - Content */}
          <div className="flex flex-col h-full space-y-6">
            {/* Title, description, and context */}
            <div className="bg-white p-6 rounded-2xl shadow-lg relative">
              <div className="absolute top-4 right-4">
                <AddToWishlistButton 
                  item={selectedImage} 
                  selections={{
                    material: selectedMaterial,
                    price: selectedPrice,
                    isCustom: showCustomOptions,
                    customWidth: showCustomOptions ? width : null,
                    customHeight: showCustomOptions ? height : null,
                    calculatedPrice: calculatedPrice
                  }}
                />
              </div>
              <h1 className="text-xl font-serif text-gray-600 mb-2 pr-12">{selectedImage.desc}</h1>
              <p className="text-base text-gray-600 mb-4 leading-relaxed font-medium">{selectedImage.title}</p>
              <p className="text-sm text-gray-500 leading-relaxed">{selectedImage.context}</p>
            </div>

            {/* Material and Price section */}
            <div className="bg-white p-6 rounded-2xl shadow-lg space-y-4">
              {/* Standard/Custom Toggle Buttons */}
              <div className="flex justify-center mb-6">
                <div className="inline-flex bg-gray-100 p-2 rounded-xl shadow-sm">
                  <button
                    onClick={() => setShowCustomOptions(false)}
                    className={`px-8 py-3 rounded-lg text-base font-semibold transition-all duration-200 mx-1
                      ${!showCustomOptions
                        ? 'bg-gray-600 text-white shadow-md transform scale-105'
                        : 'bg-gray-300 text-gray-600 hover:bg-gray-400 hover:scale-102'
                      }`}
                  >
                    Standard Sizes
                  </button>
                  <button
                    onClick={() => setShowCustomOptions(true)}
                    className={`px-8 py-3 rounded-lg text-base font-semibold transition-all duration-200 mx-3
                      ${showCustomOptions
                        ? 'bg-gray-600 text-white shadow-md transform scale-105'
                        : 'bg-gray-300 text-gray-600 hover:bg-gray-400 hover:scale-102'
                      }`}
                  >
                    Custom Size
                  </button>
                </div>
              </div>

              {/* Material Selection - Conditional rendering for mobile dropdown */}
              {isMobile ? (
                <div className="relative">
                  <label className="block font-semibold mb-2 text-sm text-gray-800">Select Material</label>
                  <button
                    onClick={() => setShowMaterialDropdown(!showMaterialDropdown)}
                    className="w-full p-3 border border-gray-300 rounded-xl bg-gray-50 flex justify-between items-center text-sm font-medium text-gray-700 focus:ring-4 focus:ring-indigo-100 focus:border-indigo-400 transition duration-300 outline-none"
                  >
                    {selectedMaterial}
                    <svg
                      className={`w-4 h-4 ml-2 transition-transform duration-300 ${showMaterialDropdown ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </button>
                  {showMaterialDropdown && (
                    <div className="absolute z-10 w-full mt-2 bg-white border border-gray-300 rounded-xl shadow-lg origin-top transition-transform duration-300 ease-out transform scale-y-100 opacity-100 animate-slide-down">
                      {Object.keys(pricingRules).map((material) => (
                        <div
                          key={material}
                          onClick={() => handleMaterialChange({ target: { value: material } })}
                          className="p-3 cursor-pointer hover:bg-gray-100 text-sm font-medium text-gray-700"
                        >
                          {material}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <label className="block font-semibold mb-2 text-sm text-gray-800">Select Material</label>
                  <select
                    value={selectedMaterial}
                    onChange={handleMaterialChange}
                    className="w-full p-3 border border-gray-300 rounded-xl bg-gray-50 focus:ring-4 focus:ring-indigo-100 focus:border-indigo-400 transition duration-300 outline-none text-sm"
                  >
                    <option value="Normal">Normal</option>
                    <option value="Textured">Textured</option>
                    <option value="Canvas texture">Canvas texture</option>
                    <option value="Rodies glitter">Rodies glitter</option>
                    <option value="Strokes">Strokes</option>
                  </select>
                </div>
              )}

              {!showCustomOptions ? (
                <div>
                  <label className="block font-semibold mb-2 text-sm text-gray-800">Price per sq. feet</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={`₹ ${selectedPrice}`}
                      readOnly
                      className="w-full p-3 border border-gray-300 rounded-xl bg-gray-100 font-semibold text-sm text-gray-700"
                    />
                    <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium text-xs">
                      per sq. feet
                    </span>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block font-semibold mb-1 text-xs text-gray-700">Width (inches)</label>
                      <input
                        type="number"
                        value={width}
                        onChange={handleWidthChange}
                        placeholder="Width in inches"
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 transition duration-300 outline-none text-sm"
                      />
                    </div>
                    <div>
                      <label className="block font-semibold mb-1 text-xs text-gray-700">Height (inches)</label>
                      <input
                        type="number"
                        value={height}
                        onChange={handleHeightChange}
                        placeholder="Height in inches"
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 transition duration-300 outline-none text-sm"
                      />
                    </div>
                  </div>

                  <button
                    onClick={calculatePrice}
                    className="w-full bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-lg transition duration-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50 text-sm"
                  >
                    Calculate Price
                  </button>

                  {calculatedPrice !== null && (
                    <div className="p-3 bg-gray-50 border border-gray-100 rounded-lg mt-4 text-center">
                      <p className="text-sm font-semibold text-gray-700 mb-2">
                        Size: {width}" × {height}"
                      </p>
                      <div className="text-center">
                        <span className="text-base font-bold text-gray-700">Total Price: </span>
                        <span className="text-2xl font-extrabold text-black-800">₹ {calculatedPrice.toLocaleString('en-IN')}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Share options */}
            <div className="flex flex-col items-center">
              <Shareooptions />
            </div>
          </div>
        </div>
        <TopSuggestions />
        <Scrollfunctions />
        <OurInformation />
      </div>
      <Footer />
    </div>
  );
};

export default Kidsdesc;
